import React from 'react'
import { Button, Divider, Flex } from 'antd'
import { EditOutlined, CloseCircleOutlined, CopyOutlined, FolderOpenOutlined } from '@ant-design/icons'
import './notification-actions.scss'

interface NotificationActionsProps {
    /** Whether the notification can be edited (status + permissions) */
    canEdit: boolean
    /** Whether the user can create (duplicate) notifications */
    canDuplicate: boolean
    /** Whether the notification can be cancelled (scheduled + permissions) */
    canCancel?: boolean
    /** Whether the draft can be archived */
    canArchive?: boolean
    onEdit: () => void
    onDuplicate: () => void
    onCancel?: () => void
    onArchive?: () => void
    className?: string
}

/**
 * Shared action button group for notification quick sheet and detail views.
 *
 * Layout: vertical stack with constructive actions first, a divider,
 * then destructive actions last (following iOS action sheet conventions).
 *
 * Order:
 * 1. Edit (primary constructive)
 * 2. Duplicate (secondary constructive)
 * --- divider ---
 * 3. Cancel / Archive (destructive)
 */
export const NotificationActions: React.FC<NotificationActionsProps> = ({
    canEdit,
    canDuplicate,
    canCancel,
    canArchive,
    onEdit,
    onDuplicate,
    onCancel,
    onArchive,
    className,
}) => {
    if (!canEdit && !canDuplicate && !canCancel && !canArchive) return null

    const hasConstructive = canEdit || canDuplicate
    const hasDestructive = canCancel || canArchive

    return (
        <Flex vertical gap="var(--space-2)" className={className}>
            {canEdit && (
                <Button icon={<EditOutlined />} onClick={onEdit} block className="notification-actions__btn">
                    Edit
                </Button>
            )}
            {canDuplicate && (
                <Button icon={<CopyOutlined />} onClick={onDuplicate} block className="notification-actions__btn">
                    Duplicate
                </Button>
            )}
            {hasConstructive && hasDestructive && <Divider className="notification-actions__divider" />}
            {canCancel && (
                <Button
                    icon={<CloseCircleOutlined />}
                    danger
                    onClick={onCancel}
                    block
                    className="notification-actions__btn"
                >
                    Cancel
                </Button>
            )}
            {canArchive && (
                <Button
                    icon={<FolderOpenOutlined />}
                    danger
                    onClick={onArchive}
                    block
                    className="notification-actions__btn"
                >
                    Archive
                </Button>
            )}
        </Flex>
    )
}
